import { useEffect, useState } from "react";
import { Check, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import { Link } from "react-router-dom";

export default function Pricing() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [plans, setPlans] = useState([]);
    const [working, setWorking] = useState(null);

    useEffect(() => {
        api.get("/plans").then((r) => setPlans(r.data.plans || [])).catch(() => {});
    }, []);

    const subscribe = async (planId) => {
        if (!user) {
            navigate("/signup");
            return;
        }
        setWorking(planId);
        try {
            const { data } = await api.post("/checkout/create-session", {
                plan_id: planId,
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Checkout failed");
            setWorking(null);
        }
    };

    const popularId = "pro";

    const Page = (
        <>
            <div className="grid md:grid-cols-3 gap-6">
                {plans.map((p) => {
                    const isPopular = p.id === popularId;
                    return (
                        <div
                            key={p.id}
                            data-testid={`pricing-card-${p.id}`}
                            className={`rounded-3xl p-8 border lift transition ${isPopular ? "border-orange-500/40 bg-gradient-to-b from-orange-500/8 to-zinc-900/40" : "border-white/8 bg-zinc-900/40"}`}
                        >
                            {isPopular && (
                                <div className="inline-block text-[10px] font-mono uppercase tracking-widest text-orange-300 bg-orange-500/15 px-2 py-1 rounded-full mb-4">
                                    Most popular
                                </div>
                            )}
                            <h3 className="font-heading text-2xl font-bold">{p.name}</h3>
                            <div className="mt-3 mb-6">
                                <span className="font-heading text-5xl font-bold">${p.price}</span>
                                <span className="text-zinc-500 ml-1">/mo</span>
                            </div>
                            <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-4">
                                {p.credits} credits / month
                            </div>
                            <ul className="space-y-3 mb-8">
                                {p.features.map((f) => (
                                    <li key={f} className="flex items-start gap-2 text-sm text-zinc-300">
                                        <Check className="w-4 h-4 mt-0.5 text-orange-400 shrink-0" />
                                        <span>{f}</span>
                                    </li>
                                ))}
                            </ul>
                            <button
                                type="button"
                                onClick={() => subscribe(p.id)}
                                disabled={!!working}
                                data-testid={`pricing-subscribe-${p.id}`}
                                className={`w-full py-3 rounded-full font-semibold transition inline-flex items-center justify-center gap-2 ${isPopular ? "btn-magic text-white" : "bg-white/5 hover:bg-white/10 text-white border border-white/10"} disabled:opacity-50`}
                            >
                                {working === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                                {working === p.id ? "Redirecting..." : user ? `Upgrade to ${p.name}` : `Start ${p.name}`}
                            </button>
                        </div>
                    );
                })}
            </div>
            <div className="text-center text-xs text-zinc-500 mt-10">
                All plans renew monthly. Cancel anytime. Test mode — no real card charge.
            </div>
        </>
    );

    if (!user) {
        return (
            <div className="min-h-screen bg-zinc-950 grain">
                <header className="fixed top-0 inset-x-0 z-40 bg-zinc-950/80 backdrop-blur-xl border-b border-white/5">
                    <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
                        <Logo size="md" />
                        <Link to="/login" className="text-sm text-zinc-300 hover:text-white">Login</Link>
                    </div>
                </header>
                <div className="max-w-6xl mx-auto px-6 pt-32 pb-20">
                    <div className="text-center mb-12">
                        <div className="text-[11px] uppercase tracking-[0.22em] text-orange-400 mb-3 font-mono">Pricing</div>
                        <h1 className="font-heading text-5xl md:text-6xl font-bold tracking-tighter">Built for every kitchen.</h1>
                        <p className="mt-5 text-zinc-400 text-lg">Try 10 enhancements free. No credit card.</p>
                    </div>
                    {Page}
                </div>
            </div>
        );
    }

    return (
        <AppShell subtitle="Pricing" title="Pick your plan.">
            {Page}
        </AppShell>
    );
}
